/**
 * Service de géolocalisation
 * Gère GPS, IP et calcul de geoHash
 */

/**
 * Encode des coordonnées GPS en geoHash selon l'algorithme Niemeyer
 * @param {number} latitude - Latitude (-90 à 90)
 * @param {number} longitude - Longitude (-180 à 180)
 * @param {number} precision - Nombre de caractères du geoHash (défaut: 9 pour ±5m)
 * @returns {string} GeoHash encodé
 *
 * Exemples:
 * - Abidjan (5.3470, -3.9660) → "ebvnt3mwc"
 * - Paris (48.8566, 2.3522) → "u09tunqu0"
 */
export function encodeGeoHash(latitude, longitude, precision = 9) {
  const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
  let idx = 0; // Index pour construire le caractère BASE32
  let bit = 0; // Compteur de bits (0-4)
  let evenBit = true; // Alterne entre longitude (true) et latitude (false)
  let geoHash = "";

  // Limites de la zone géographique
  let latMin = -90, latMax = 90;
  let lonMin = -180, lonMax = 180;

  // Construire le geoHash caractère par caractère
  while (geoHash.length < precision) {
    if (evenBit) {
      // Traiter la longitude
      const lonMid = (lonMin + lonMax) / 2;
      if (longitude > lonMid) {
        idx = (idx << 1) + 1; // Ajouter bit 1
        lonMin = lonMid; // Réduire la zone
      } else {
        idx = (idx << 1) + 0; // Ajouter bit 0
        lonMax = lonMid;
      }
    } else {
      // Traiter la latitude
      const latMid = (latMin + latMax) / 2;
      if (latitude > latMid) {
        idx = (idx << 1) + 1;
        latMin = latMid;
      } else {
        idx = (idx << 1) + 0;
        latMax = latMid;
      }
    }

    evenBit = !evenBit; // Alterner

    // Après 5 bits, convertir en caractère BASE32
    if (++bit === 5) {
      geoHash += BASE32[idx];
      bit = 0;
      idx = 0;
    }
  }

  return geoHash;
}

/**
 * Validation des coordonnées géographiques
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} true si valides
 */
export function isValidCoordinates(lat, lng) {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !isNaN(lat) &&
    !isNaN(lng)
  );
}

/**
 * Obtenir la position GPS avec système de retry intelligent
 * Utilise exponential backoff : 2s, 4s, 6s, 8s, 10s
 *
 * @param {object} options - Options de géolocalisation
 * @param {number} maxRetries - Nombre maximum de tentatives (défaut: 5)
 * @returns {Promise<{success: boolean, data?: object, error?: string, shouldRetry?: boolean}>}
 */
export async function getCurrentPositionWithRetry(
  options = {
    enableHighAccuracy: true,
    timeout: 45000, // 45 secondes
    maximumAge: 30000 // Cache de 30 secondes
  },
  maxRetries = 5
) {
  let attempt = 0;

  /**
   * Fonction interne pour tenter d'obtenir la position
   */
  const tryGetPosition = () => {
    return new Promise((resolve) => {
      // Vérifier si géolocalisation disponible
      if (!navigator.geolocation) {
        resolve({
          success: false,
          error: 'geolocation_not_supported',
          shouldRetry: false
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        // Succès
        (position) => {
          resolve({
            success: true,
            data: {
              coordinates: [position.coords.longitude, position.coords.latitude],
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp
            }
          });
        },
        // Erreur
        (error) => {
          const errorCode = error.code;
          const errorMessages = {
            1: 'permission_denied',
            2: 'position_unavailable',
            3: 'timeout'
          };

          const errorType = errorMessages[errorCode] || 'unknown';
          console.error(`[LocationService] GPS error on attempt ${attempt + 1}:`, errorType);

          // Permission refusée → pas de retry
          if (errorCode === 1) {
            resolve({
              success: false,
              error: errorType,
              shouldRetry: false
            });
            return;
          }

          // Timeout ou position unavailable → retry possible
          if (attempt < maxRetries) {
            attempt++;
            const delay = 2000 * attempt; // 2s, 4s, 6s, 8s, 10s

            setTimeout(() => {
              tryGetPosition().then(resolve);
            }, delay);
          } else {
            // Max retries atteints
            console.error('[LocationService] Max GPS retries reached');
            resolve({
              success: false,
              error: errorType,
              shouldRetry: false
            });
          }
        },
        options
      );
    });
  };

  return tryGetPosition();
}

/**
 * Obtenir la localisation approximative via l'adresse IP
 * Utilise l'API gratuite ipapi.co (1000 requêtes/jour)
 *
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export async function getLocationFromIP() {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();

    // Vérifier si on a les données nécessaires
    if (!data.latitude || !data.longitude) {
      throw new Error('Invalid IP location data');
    }

    return {
      success: true,
      data: {
        coordinates: [data.longitude, data.latitude],
        city: data.city,
        region: data.region,
        country: data.country_code,
        countryName: data.country_name,
        accuracy: 10000, // ~10km (précision IP)
        ip: data.ip
      }
    };
  } catch (error) {
    console.error('[LocationService] IP geolocation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Détecter le navigateur et l'OS de l'utilisateur
 * @returns {{os: string, browser: string}}
 */
export function getBrowserInfo() {
  const ua = navigator.userAgent;
  let os = 'unknown';
  let browser = 'unknown';

  // Détecter l'OS
  if (ua.indexOf('Android') > -1) {
    os = 'android';
  } else if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    os = 'ios';
  } else if (ua.indexOf('Windows') > -1) {
    os = 'windows';
  } else if (ua.indexOf('Mac') > -1) {
    os = 'macos';
  } else if (ua.indexOf('Linux') > -1) {
    os = 'linux';
  }

  // Détecter le navigateur
  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
    browser = 'chrome';
  } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browser = 'safari';
  } else if (ua.indexOf('Firefox') > -1) {
    browser = 'firefox';
  } else if (ua.indexOf('Edg') > -1) {
    browser = 'edge';
  }

  return { os, browser };
}

/**
 * Obtenir les instructions pour activer le GPS selon l'OS et le navigateur
 * @param {string} os - Système d'exploitation
 * @param {string} browser - Navigateur
 * @returns {Array<string>} Liste des étapes
 */
export function getGPSInstructions(os, browser) {
  // Instructions pour Chrome sur Android
  if (os === 'android' && browser === 'chrome') {
    return [
      "Appuyez sur l'icône 🔒 à gauche de l'adresse du site",
      "Sélectionnez 'Paramètres du site' ou 'Autorisations'",
      "Trouvez 'Position' et changez à 'Autoriser'",
      "Rafraîchissez la page"
    ];
  }

  // Instructions pour Safari sur iOS
  if (os === 'ios' && browser === 'safari') {
    return [
      "Ouvrez l'app Réglages de votre iPhone/iPad",
      "Faites défiler et sélectionnez 'Safari'",
      "Appuyez sur 'Localisation'",
      "Sélectionnez 'Toujours autoriser' ou 'Autoriser'",
      "Revenez dans l'application"
    ];
  }

  // Instructions pour Chrome sur Desktop
  if ((os === 'windows' || os === 'macos' || os === 'linux') && browser === 'chrome') {
    return [
      "Cliquez sur l'icône 🔒 à gauche de l'adresse",
      "Sélectionnez 'Paramètres du site'",
      "Trouvez 'Position' et changez à 'Autoriser'",
      "Rafraîchissez la page (F5)"
    ];
  }

  // Instructions pour Firefox
  if (browser === 'firefox') {
    return [
      "Cliquez sur l'icône d'information (i) dans la barre d'adresse",
      "Cliquez sur la flèche à côté de 'Autorisations'",
      "Décochez 'Bloquer' pour 'Accéder à votre position'",
      "Rafraîchissez la page (F5)"
    ];
  }

  // Instructions génériques
  return [
    "Cliquez sur l'icône de localisation ou de cadenas dans la barre d'adresse",
    "Autorisez l'accès à votre position pour ce site",
    "Assurez-vous que le GPS de votre appareil est activé",
    "Rafraîchissez cette page"
  ];
}

/**
 * Vérifier si on peut utiliser le cache de localisation
 * @param {number} lastUpdate - Timestamp de la dernière mise à jour
 * @param {number} maxAge - Age maximum en heures (défaut: 24h)
 * @returns {boolean} true si le cache est encore valide
 */
export function isCacheValid(lastUpdate, maxAge = 24) {
  if (!lastUpdate) return false;

  const hoursSinceUpdate = (Date.now() - lastUpdate) / (1000 * 60 * 60);
  return hoursSinceUpdate < maxAge;
}

/**
 * Calculer la distance entre deux coordonnées en mètres (formule Haversine)
 * @param {Array<number>} coords1 - [longitude, latitude]
 * @param {Array<number>} coords2 - [longitude, latitude]
 * @returns {number} Distance en mètres
 */
export function calculateDistanceMeters(coords1, coords2) {
  const [lon1, lat1] = coords1;
  const [lon2, lat2] = coords2;

  const R = 6371e3; // Rayon de la Terre en mètres
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance en mètres
}
